import { Chip, cn } from "@moanhot/ui";
import type { AdminReconciliationItem } from "@/api/adminClient";
import { NUM, TD, TH, dollars, when } from "@/screens/format";
import { useAdminLoad } from "@/screens/hooks";
import { EmptyNote, LoadState, Page, Table } from "@/screens/ui";

const FLAG: Record<AdminReconciliationItem["flag"], { label: string; help: string }> = {
  partially_paid: { label: "Partially paid", help: "Held, not credited: paid short, accepted by hand at the provider, or paid a different amount. Check it at the provider, then credit by an adjustment, or not." },
  credited_not_in_ledger: { label: "Credited, not in ledger", help: "The invoice says credited but the ledger has no top-up." },
  ledger_not_credited: { label: "In ledger, not credited", help: "The ledger has a top-up for an invoice that is not credited." },
  amount_differs: { label: "Amount differs", help: "The credited amount and the ledger top-up disagree." },
};

/** The payment provider as the owner knows it from its own dashboard. */
const PROVIDER: Record<string, string> = { oxapay: "OxaPay", nowpayments: "NOWPayments", plisio: "Plisio" };

/** Top-up invoices that the ledger and the provider disagree on, for an owner to settle. */
export function Reconciliation() {
  const [load] = useAdminLoad((a) => a.reconciliation(), [], "Could not load reconciliation.");
  return (
    <Page title="Reconciliation">
      <p className="-mt-3 mb-6 max-w-[72ch] text-sm text-ink-2">
        Settle each one with an owner adjustment on the tenant, naming the invoice in the reason.
      </p>
      <LoadState load={load} empty={(d) => (d.data.length === 0 ? <EmptyNote title="Everything reconciles" body="No top-up needs a hand right now." /> : null)}>
        {(d) => (
          <Table
            label="Invoices to settle"
            head={
              <>
                <th className={TH}>Invoice</th>
                <th className={TH}>Tenant</th>
                <th className={TH}>Flag</th>
                <th className={cn(TH, "text-right")}>Invoiced</th>
                <th className={cn(TH, "text-right")}>Credited</th>
                <th className={cn(TH, "text-right")}>Ledger</th>
              </>
            }
          >
            {d.data.map((r) => (
              <tr key={r.invoice_id}>
                <td className={cn(TD, "font-mono text-xs")}>
                  {r.invoice_id}
                  <div className="font-sans text-ink-3">
                    {PROVIDER[r.provider] ?? r.provider}, {when(r.created_at)}
                  </div>
                </td>
                <td className={TD}>
                  {r.tenant_name}
                  <div className="font-mono text-xs text-ink-3">{r.tenant_id}</div>
                </td>
                <td className={TD}>
                  <Chip className="text-warn">{FLAG[r.flag].label}</Chip>
                  <div className="mt-1 max-w-[40ch] text-xs text-ink-3">{FLAG[r.flag].help}</div>
                </td>
                <td className={NUM}>{dollars(r.amount_requested_micro_usd)}</td>
                <td className={NUM}>{r.amount_credited_micro_usd === null ? "" : dollars(r.amount_credited_micro_usd)}</td>
                <td className={NUM}>{r.ledger_micro_usd === null ? "" : dollars(r.ledger_micro_usd)}</td>
              </tr>
            ))}
          </Table>
        )}
      </LoadState>
    </Page>
  );
}
